import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useLocalParticipant,
  useRemoteParticipants,
  useTracks,
} from '@livekit/components-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { ConnectionState, Track } from 'livekit-client';

export type LiveKitCallMediaProps = {
  expectedPeerIdentity: string;
  onDisconnected: () => void;
  onError: (error: Error) => void;
  onReconnected: () => void;
  onReconnecting: () => void;
  onRemoteAudioEnded: () => void;
  onRemoteAudioReady: () => void;
  publishAudio: boolean;
  token: string;
  url: string;
};

type LiveKitCallBridgeProps = Pick<
  LiveKitCallMediaProps,
  | 'expectedPeerIdentity'
  | 'onDisconnected'
  | 'onError'
  | 'onReconnected'
  | 'onReconnecting'
  | 'onRemoteAudioEnded'
  | 'onRemoteAudioReady'
  | 'publishAudio'
>;

const reconnectingStates = new Set([
  ConnectionState.Reconnecting,
  ConnectionState.SignalReconnecting,
]);
const remoteParticipantGraceMs = 1000;

const LiveKitCallBridge = ({
  expectedPeerIdentity,
  onDisconnected,
  onError,
  onReconnected,
  onReconnecting,
  onRemoteAudioEnded,
  onRemoteAudioReady,
  publishAudio,
}: LiveKitCallBridgeProps) => {
  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const microphoneTracks = useTracks([Track.Source.Microphone], {
    onlySubscribed: true,
  });
  const previousConnectionStateRef = useRef(connectionState);
  const hasConnectedRef = useRef(false);
  const remoteAudioEndedRef = useRef(false);
  const remoteAudioReadyRef = useRef(false);

  const expectedPeerAudioReady = useMemo(
    () =>
      microphoneTracks.some(
        ({ participant }) => participant.identity === expectedPeerIdentity,
      ),
    [expectedPeerIdentity, microphoneTracks],
  );
  const expectedPeerPresent = useMemo(
    () =>
      remoteParticipants.some(
        (participant) => participant.identity === expectedPeerIdentity,
      ),
    [expectedPeerIdentity, remoteParticipants],
  );

  useEffect(() => {
    if (expectedPeerAudioReady) {
      if (!remoteAudioReadyRef.current) {
        remoteAudioReadyRef.current = true;
        onRemoteAudioReady();
      }
      return;
    }

    if (
      connectionState !== ConnectionState.Connected ||
      expectedPeerPresent ||
      !remoteAudioReadyRef.current ||
      remoteAudioEndedRef.current
    )
      return;

    const timer = setTimeout(() => {
      remoteAudioEndedRef.current = true;
      onRemoteAudioEnded();
    }, remoteParticipantGraceMs);
    return () => clearTimeout(timer);
  }, [
    connectionState,
    expectedPeerAudioReady,
    expectedPeerPresent,
    onRemoteAudioEnded,
    onRemoteAudioReady,
  ]);

  useEffect(() => {
    const shouldPublish =
      publishAudio && connectionState === ConnectionState.Connected;

    void localParticipant.setMicrophoneEnabled(shouldPublish).catch(onError);
  }, [connectionState, localParticipant, onError, publishAudio]);

  useEffect(() => {
    const previousConnectionState = previousConnectionStateRef.current;
    const wasReconnecting = reconnectingStates.has(previousConnectionState);
    const isReconnecting = reconnectingStates.has(connectionState);

    if (connectionState === ConnectionState.Connected) {
      if (hasConnectedRef.current && wasReconnecting) {
        onReconnected();
      }
      hasConnectedRef.current = true;
    } else if (isReconnecting && !wasReconnecting) {
      onReconnecting();
    } else if (
      connectionState === ConnectionState.Disconnected &&
      hasConnectedRef.current &&
      previousConnectionState !== ConnectionState.Disconnected
    ) {
      onDisconnected();
    }

    previousConnectionStateRef.current = connectionState;
  }, [connectionState, onDisconnected, onReconnected, onReconnecting]);

  return <RoomAudioRenderer />;
};

export const LiveKitCallMedia = ({
  expectedPeerIdentity,
  onDisconnected,
  onError,
  onReconnected,
  onReconnecting,
  onRemoteAudioEnded,
  onRemoteAudioReady,
  publishAudio,
  token,
  url,
}: LiveKitCallMediaProps) => (
  <LiveKitRoom
    audio={false}
    connect
    onError={onError}
    serverUrl={url}
    token={token}
    video={false}
  >
    <LiveKitCallBridge
      expectedPeerIdentity={expectedPeerIdentity}
      onDisconnected={onDisconnected}
      onError={onError}
      onReconnected={onReconnected}
      onReconnecting={onReconnecting}
      onRemoteAudioEnded={onRemoteAudioEnded}
      onRemoteAudioReady={onRemoteAudioReady}
      publishAudio={publishAudio}
    />
  </LiveKitRoom>
);
